import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  isApiError,
  normalizeError,
  type ApiError,
  type FieldValidationError,
} from '../lib/api/errors';
import { toastApiError } from '../lib/api/error-toast';

type UseApiMutationOptions<TData, TVariables, TOnMutateResult> = Omit<
  UseMutationOptions<TData, ApiError, TVariables, TOnMutateResult>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Override the auto-error toast (pass false to suppress) */
  showErrorToast?: boolean;
};

/**
 * Wraps useMutation with ApiError typing and automatic error toast via Sonner.
 * Passes onSuccess through so callers can invalidate queries or navigate after
 * a successful mutation without losing the typed error benefit.
 */
export function useApiMutation<TData, TVariables = void, TOnMutateResult = unknown>(
  options: UseApiMutationOptions<TData, TVariables, TOnMutateResult>,
): UseMutationResult<TData, ApiError, TVariables, TOnMutateResult> {
  const { showErrorToast = true, mutationFn, onError, ...rest } = options;

  return useMutation<TData, ApiError, TVariables, TOnMutateResult>({
    ...rest,
    mutationFn: async (variables) => {
      try {
        return await mutationFn(variables);
      } catch (err) {
        const apiError = isApiError(err) ? err : normalizeError(err);
        throw apiError;
      }
    },
    onError: (error, variables, onMutateResult, context) => {
      const fieldErrors = error.fieldErrors;
      if (showErrorToast) {
        if (fieldErrors && fieldErrors.length > 0) {
          toastApiError(error, validationToastMessage(fieldErrors));
        } else if (error.code !== 'CANCELED') {
          toastApiError(error);
        }
      }

      if (import.meta.env.DEV) {
        console.error('[Mutation error]', error);
      }

      onError?.(error, variables, onMutateResult, context);
    },
  });
}

/**
 * What the operator is told when the server rejects specific fields.
 *
 * This used to read "Fix N fields below" for anything but a single field, and
 * both halves of that could be false. It named nothing, so a form with a dozen
 * inputs gave no starting point; and "below" asserted the fields are ON THE
 * SCREEN, which is only true when the rejected field is one the form renders.
 * A server DTO that omits a field the client sends — `displayConfig.lineDetail`
 * on an estimate line, which no estimate form shows — rejects the save naming
 * that field, and the operator was told to go fix two things they could neither
 * see nor change. It was unactionable in exactly the case where the operator
 * most needs to know the problem is not theirs.
 *
 * So: name them, and stop claiming where they are. Beyond three the list stops
 * being readable in a toast, and a count is the honest summary of the rest.
 */
export function validationToastMessage(fieldErrors: FieldValidationError[]): string {
  // Dedupe on the PATH, never on the label. Two paths routinely render to one
  // label — `serviceAddress.city` and `billingAddress.city` are both "City" —
  // and collapsing those would report one failing field when two failed, which
  // is a worse lie than the count it replaced.
  const paths = [...new Set(fieldErrors.map((entry) => entry.field))];
  if (paths.length === 1 && paths[0] === 'contactChannelValidation') {
    return 'Enter a phone number or email address.';
  }
  const labels = describeFields(paths);
  const NAMED = 3;

  if (labels.length === 1) return `Fix the ${labels[0]} field`;

  if (labels.length <= NAMED) {
    const last = labels[labels.length - 1];
    return `Fix the ${labels.slice(0, -1).join(', ')} and ${last} fields`;
  }

  const remaining = labels.length - NAMED;
  const more = `${remaining} more field${remaining === 1 ? '' : 's'}`;
  return `Fix the ${labels.slice(0, NAMED).join(', ')} and ${more}`;
}

/**
 * Field paths as text, kept distinguishable FROM EACH OTHER.
 *
 * A leaf label is only enough while it is unique in the message. When two paths
 * land on the same one, the operator is looking at a list that names the same
 * thing twice and cannot tell which two inputs failed — so the colliding
 * entries, and only those, are qualified by the object they belong to:
 * "City (service address)" beside "City (billing address)".
 */
function describeFields(paths: string[]): string[] {
  const labels = paths.map(describeField);
  const occurrences = new Map<string, number>();
  for (const label of labels) {
    occurrences.set(label, (occurrences.get(label) ?? 0) + 1);
  }

  return labels.map((label, index) => {
    if ((occurrences.get(label) ?? 0) < 2) return label;
    const context = ancestorContext(paths[index] ?? '');
    return context ? `${label} (${context})` : label;
  });
}

/**
 * Everything the field hangs off, as lower-case prose — `serviceAddress.city`
 * gives "service address", `serviceLocation.address.city` gives "service
 * location address". The WHOLE chain, not the nearest parent: one level up is
 * only enough while that level is itself unique, and two fields under different
 * grandparents but a same-named parent would both qualify to "(address)" and
 * still name the same thing twice.
 *
 * Empty when the path has no ancestors, the one case a collision cannot be
 * resolved this way.
 */
function ancestorContext(path: string): string {
  const ancestors = path.split('.').filter((segment) => !/^\d+$/.test(segment));
  ancestors.pop();
  return ancestors.map((segment) => asProse(labelForField(segment))).join(' ');
}

/** Lower-cased word by word, leaving acronyms alone — "GL account" is a name. */
function asProse(label: string): string {
  return label
    .split(' ')
    .map((word) => (word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(' ');
}

/** What one element of a collection is called: `lineItems.3` is "line 4". */
const COLLECTION_NOUNS: Record<string, string> = { lineItems: 'line' };

/**
 * A field path as something the operator can go and find.
 *
 * Position is the part that matters: `lineItems.3.basePrice` and
 * `lineItems.7.basePrice` are two different problems, and calling both "Price"
 * sends someone hunting through every line. Indices are 0-based on the wire and
 * 1-based on the screen.
 *
 * EVERY index in the path is named, not just the innermost. A proposal posts
 * `options.<n>.lineItems.<m>`, so reading only the last one describes the same
 * line of two different options identically — the exact ambiguity this exists
 * to remove, in a shape that ships.
 */
function describeField(field: string): string {
  const segments = field.split('.');
  const positions = segments.flatMap((segment, at) => {
    const next = segments[at + 1];
    if (/^\d+$/.test(segment) || !next || !/^\d+$/.test(next)) return [];
    return [`${collectionNoun(segment)} ${Number(next) + 1}`];
  });

  const label = labelForField(field);
  return positions.length > 0 ? `${label} on ${positions.join(', ')}` : label;
}

function collectionNoun(segment: string): string {
  return COLLECTION_NOUNS[segment] ?? asProse(labelForField(segment)).replace(/s$/, '');
}

const FIELD_LABELS: Record<string, string> = {
  basePrice: 'Price',
  laborRate: 'Labor rate',
  costBasis: 'Unit cost',
  markupPercent: 'Markup',
  priceStrategy: 'Billing type',
  taxLineType: 'Tax line type',
  taxCategory: 'Tax category',
  taxCode: 'Tax code',
  glAccount: 'GL account',
  glClass: 'GL class',
  glCogs: 'GL COGS account',
  glExpenseAccount: 'GL expense account',
  businessUnitId: 'Business unit',
  jobTypeId: 'Job type',
  displayOrder: 'Display order',
  // Storage names the decision `pricingTreatment`; the operator has only ever
  // seen the Charge control on a service's parts, reading Included / Billed
  // extra. Name what they can go and look at.
  pricingTreatment: 'Charge (Included / Billed extra)',
  showInDetail: 'Customer visibility',
  chargeable: 'Charge',
  retrievalBoost: 'Retrieval boost',
  hazardLevel: 'Hazard level',
  itemId: 'Item',
};

function labelForField(field: string): string {
  const leaf = field
    .split('.')
    .filter((part) => !/^\d+$/.test(part))
    .at(-1);
  if (!leaf) return field;
  if (FIELD_LABELS[leaf]) return FIELD_LABELS[leaf];
  return leaf
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}
